import { SisetuResult } from 'interface/sisetu'

import {
  Box,
  Stack,
  ListItem,
  ListItemProps,
  ListItemButton,
  Typography,
  LinearProgress,
  LinearProgressProps,
} from '@mui/material'

import { MedalsIcon } from 'shared/icons'

const range = (n: number, min: number, max: number) => {
  return Math.max(0, Math.min(100, ((n - min) * 100) / (max - min)))
}

type LinearProgressWithLabelProps = {
  caption: string
  rank: string
} & LinearProgressProps
const LinearProgressWithLabel = ({
  caption,
  rank,
  ...others
}: LinearProgressWithLabelProps) => (
  <Stack direction="row" alignItems="center">
    <Typography
      variant="caption"
      component="div"
      align="right"
      sx={{ minWidth: '4em', whiteSpace: 'nowrap', mr: 0.5, lineHeight: 1.2 }}
    >
      {caption}
    </Typography>
    <LinearProgress
      {...others}
      variant="determinate"
      sx={{
        width: '100%',
        height: '0.6em',
        borderRadius: 1,
      }}
    />
    <Typography
      variant="caption"
      component="div"
      color="text.secondary"
      sx={{
        minWidth: '3.5em',
        textAlign: 'right',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
      }}
    >
      {rank}
    </Typography>
  </Stack>
)

type SisetuListRecordProps = {
  sisetu: SisetuResult
  rank?: number
  selected?: boolean
  onSelectSisetu?: (sisetuCode: string) => void
} & ListItemProps
export const SisetuListRecord = ({
  sisetu,
  rank,
  selected,
  onSelectSisetu,
  ...others
}: SisetuListRecordProps) => (
  <ListItem disablePadding {...others}>
    <ListItemButton
      selected={selected}
      onClick={() => {
        if (onSelectSisetu) {
          onSelectSisetu(sisetu.sisetuCode ?? '')
        }
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {rank === 1 ? (
            <MedalsIcon sx={{ color: 'gold', display: 'block', margin: 'auto' }} fontSize="medium"/>
          ) : rank === 2 ? (
            <MedalsIcon sx={{ color: 'silver', display: 'block', margin: 'auto' }} fontSize="medium"/>
          ) : rank === 3 ? (
            <MedalsIcon sx={{ color: 'brown', display: 'block', margin: 'auto' }} />
          ) : (
            <Typography variant="body2" whiteSpace='nowrap'>
              {rank ? `${rank}位` : '--'}
            </Typography>
          )}
          <Typography variant="body1" component="div" color="text.primary" flexGrow={1}>
            {sisetu.sisetuName}
          </Typography>
        </Stack>

        <Typography variant="body2" component="div" color="text.secondary">
          {sisetu.juusyo}
        </Typography>

        <Stack sx={{ width: '100%' }} marginTop={0.5}>
          {sisetu.score?.dpc ? (
            <LinearProgressWithLabel
              color="dpc"
              caption="DPC"
              value={range(sisetu.score.dpc.deviation, 40, 120)}
              rank={`${sisetu.score.dpc.rank}位`}
            />
          ) : (
            <></>
          )}
          {sisetu.score?.cancer ? (
            <LinearProgressWithLabel
              color="cancer"
              caption="がん"
              value={range(sisetu.score.cancer.deviation, 40, 120)}
              rank={`${sisetu.score.cancer.rank}位`}
            />
          ) : (
            <></>
          )}
          {sisetu.score?.trial ? (
            <LinearProgressWithLabel
              color="trial"
              caption="治験"
              value={range(sisetu.score.trial.deviation, 40, 120)}
              rank={`${sisetu.score.trial.rank}位`}
            />
          ) : (
            <></>
          )}
          {sisetu.score?.journal ? (
            <LinearProgressWithLabel
              color="journal"
              caption="論文"
              value={range(sisetu.score.journal.deviation, 40, 120)}
              rank={`${sisetu.score.journal.rank}位`}
            />
          ) : (
            <></>
          )}
          {sisetu.score?.senmoni ? (
            <LinearProgressWithLabel
              color="senmoni"
              caption="専門医"
              value={range(sisetu.score.senmoni.deviation, 40, 120)}
              rank={`${sisetu.score.senmoni.rank}位`}
            />
          ) : (
            <></>
          )}
          {sisetu.score ? (
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              sx={{
                minWidth: '3.5em',
                textAlign: 'right',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
              }}
            >
              {'総合 ' + sisetu.score.rank + '位'}
            </Typography>
          ) : (
            <></>
          )}
        </Stack>
      </Box>
    </ListItemButton>
  </ListItem>
)
